import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";
import { FileTextIcon as FileText, ArrowSquareOutIcon as ArrowSquareOut, UploadSimpleIcon as UploadSimple, TrashSimpleIcon as TrashSimple } from "@phosphor-icons/react";

/**
 * Optional reusable contract TEMPLATE for breeders — one per kennel.
 *
 * Most breeders have a single sales-agreement template their lawyer
 * drafted; they tweak the buyer name + price + dates per puppy. This
 * card lives on /settings (Profile tab) so the breeder uploads the
 * template once. Per-puppy contracts are still uploaded individually
 * (see SaleContractCard) — this template is just a starting point so
 * the breeder doesn't lose track of the source document.
 */
export default function KennelDefaultContractCard() {
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/contracts/kennel-default", { silent: true });
      setTpl(data);
    } catch {
      setTpl(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contracts/kennel-default", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setTpl(data);
      toast.success("Template saved. Use it as your starting point per puppy.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async () => {
    if (!confirm("Remove your stored contract template?")) return;
    setBusy(true);
    try {
      await api.delete("/contracts/kennel-default");
      setTpl(null);
      toast.success("Template removed.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-white border border-[#EAEAEA] rounded-xl p-5"
      data-testid="kennel-default-contract-card"
    >
      <div className="flex items-start gap-4">
        <FileText size={24} weight="duotone" className="text-gold mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-spectral text-lg">My standard sales contract</h3>
          <p className="text-[13px] text-[#6B6B6B] mt-1 leading-relaxed">
            Optional. Upload a reusable template once (your lawyer's version)
            so you can pull it up from any puppy listing. You still upload a
            buyer-specific PDF on each puppy — this is just the starting point.
          </p>

          {tpl ? (
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              <a
                href={tpl.pdf_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-ink underline hover:text-crimson"
                data-testid="kennel-default-contract-link"
              >
                {tpl.filename || "View template"} <ArrowSquareOut size={12} />
              </a>
              <span className="text-[11px] text-[#6B6B6B]">
                Uploaded {tpl.uploaded_at?.slice(0, 10)}
              </span>
              <Button
                onClick={() => fileRef.current?.click()}
                variant="outline"
                size="sm"
                className="rounded-full"
                disabled={busy}
                data-testid="kennel-default-contract-replace"
              >
                <UploadSimple size={12} weight="bold" /> Replace
              </Button>
              <Button
                onClick={onDelete}
                variant="outline"
                size="sm"
                disabled={busy}
                className="rounded-full text-crimson border-crimson/40 hover:bg-crimson/5"
                data-testid="kennel-default-contract-delete"
              >
                <TrashSimple size={12} weight="bold" /> Remove
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="hidden"
                onChange={(e) => onUpload(e.target.files?.[0])}
              />
            </div>
          ) : (
            <div className="mt-4">
              <Label className="text-[11px] uppercase tracking-wider text-[#6B6B6B] mb-1 block">
                Upload template PDF
              </Label>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => onUpload(e.target.files?.[0])}
                disabled={busy}
                data-testid="kennel-default-contract-upload"
                className="text-[12px]"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
